import { useState, useEffect, useCallback, useMemo } from 'react';
import { getShifts, updateShift, Shift, getRosterVersions, RosterVersion, publishRosterVersion, unpublishRosterVersion, checkCoverage, CoverageReport, getCalendarRange } from '../../services/rosterService';
import { format, eachDayOfInterval, startOfWeek, endOfWeek } from 'date-fns';
import { DndContext, DragEndEvent, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { useAuth } from '../../contexts/AuthContext';
import ShiftItem from './ShiftItem';
import CoverageWidget from './CoverageWidget';
import WeeklyRoster from './WeeklyRoster';
import MonthlyRosterView from './MonthlyRosterView';
import CreateShiftModal from './CreateShiftModal';
import PatternGenerationModal from './PatternGenerationModal';
import BulkAssignmentModal from './BulkAssignmentModal';
import VersionControlPanel from './VersionControlPanel';
import RosterWizard from './RosterWizard';
import { getRosterVersionLabel } from '../../utils/rosterVersionLabel';

// Debounce utility
const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    return (...args: Parameters<T>) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

const RosterDashboard = () => {
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState<'month' | 'week'>('month');
    const [activeShift, setActiveShift] = useState<Shift | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showPatternModal, setShowPatternModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [currentVersion, setCurrentVersion] = useState<RosterVersion | null>(null);
    const [versions, setVersions] = useState<RosterVersion[]>([]);
    const [coverageReports, setCoverageReports] = useState<Map<string, CoverageReport>>(new Map());
    const [error, setError] = useState<string | null>(null);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [versionActionLoading, setVersionActionLoading] = useState(false);
    const [showFullscreenRoster, setShowFullscreenRoster] = useState(false);

    useEffect(() => {
        // Debug/Silence unused variable warning
        if (coverageReports.size > 0) console.debug('Coverage reports updated:', coverageReports.size);
    }, [coverageReports]);

    // Check if user is admin/manager (can create rosters)
    const canCreateRoster = useMemo(() => {
        if (!user) return false;
        const userRole = typeof user.role === 'string' ? user.role : user.role?.name || '';
        const adminRoles = ['Super Admin', 'Station Manager', 'System Administrator', 'Administrator', 'ADMIN'];
        return adminRoles.includes(userRole);
    }, [user]);

    const isVersionForDisplayedMonth = useCallback((version: RosterVersion) => {
        const versionStart = new Date(version.startDate);
        return versionStart.getUTCFullYear() === currentDate.getFullYear()
            && versionStart.getUTCMonth() === currentDate.getMonth();
    }, [currentDate]);
    const versionsForDisplayedMonth = useMemo(
        () => versions.filter(isVersionForDisplayedMonth),
        [versions, isVersionForDisplayedMonth]
    );
    const displayedVersion = currentVersion && isVersionForDisplayedMonth(currentVersion)
        ? currentVersion
        : null;
    const displayedVersionLabel = displayedVersion
        ? getRosterVersionLabel(displayedVersion, versionsForDisplayedMonth)
        : null;

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
    );

    const loadShifts = useCallback(async (versionId?: string) => {
        setLoading(true);
        setError(null);
        try {
            let start: Date;
            let end: Date;

            if (view === 'week') {
                // Compute week range client-side (Mon–Sun) from the actual currentDate.
                // This matches WeeklyRoster's own week boundaries and ensures navigation
                // (prev/next week) correctly shifts the fetched range.
                start = startOfWeek(currentDate, { weekStartsOn: 1 });
                end = endOfWeek(currentDate, { weekStartsOn: 1 });
            } else {
                // Month view: ask server for the month range (keeps existing behaviour).
                const year = currentDate.getFullYear();
                const month = currentDate.getMonth() + 1;
                const range = await getCalendarRange(year, month, view);

                const parseAsLocal = (isoString: string) => {
                    const [datePart] = isoString.split('T');
                    const [y, m, d] = datePart.split('-').map(Number);
                    return new Date(y, m - 1, d);
                };

                start = parseAsLocal(range.start);
                const [endDatePart] = range.end.split('T');
                const [ey, em, ed] = endDatePart.split('-').map(Number);
                end = new Date(ey, em - 1, ed, 23, 59, 59, 999);
            }

            // Store the date range for calendar rendering
            setDateRange({ start, end });

            if (!versionId) {
                setShifts([]);
                return;
            }

            const data = await getShifts(start, end, undefined, versionId);
            setShifts(data);
        } catch (error: any) {
            console.error("Failed to load shifts", error);
            console.error("Error details:", {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                data: error.response?.data
            });
            const errorMessage = error.response?.data?.error?.message
                || error.response?.data?.message
                || error.message
                || 'Failed to load shifts';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [currentDate, view]);

    const selectVersionForDisplayedPeriod = useCallback((availableVersions: RosterVersion[]) => {
        const matchingVersions = availableVersions
            .filter(isVersionForDisplayedMonth)
            .sort((left, right) =>
                Number(right.status === 'PUBLISHED') - Number(left.status === 'PUBLISHED')
                || right.versionNumber - left.versionNumber
            );

        setCurrentVersion((selected) => {
            const selectedForPeriod = matchingVersions.find((version) => version.id === selected?.id);
            if (selectedForPeriod) {
                return selectedForPeriod;
            }
            return matchingVersions[0] ?? null;
        });
    }, [isVersionForDisplayedMonth]);

    const loadVersions = useCallback(async () => {
        try {
            const data = await getRosterVersions();
            setVersions(data);
            selectVersionForDisplayedPeriod(data);
        } catch (error: any) {
            console.error("Failed to load versions", error);
            // Don't show error for versions - it's optional
        }
    }, [selectVersionForDisplayedPeriod]);

    const loadCoverageForDay = useCallback(async (date: Date) => {
        try {
            const report = await checkCoverage(date);
            setCoverageReports(prev => {
                const newMap = new Map(prev);
                newMap.set(date.toISOString().split('T')[0], report);
                return newMap;
            });
        } catch (error) {
            console.error("Failed to load coverage", error);
        }
    }, []);

    // Debounced coverage loading
    const debouncedLoadCoverage = useMemo(
        () => debounce(loadCoverageForDay, 300),
        [loadCoverageForDay]
    );

    useEffect(() => {
        loadShifts(displayedVersion?.id);
    }, [currentDate, view, displayedVersion, loadShifts]);

    useEffect(() => {
        loadVersions();
    }, [loadVersions]);

    useEffect(() => {
        // Load coverage for visible days using server-provided date range
        if (dateRange) {
            const days = eachDayOfInterval({ start: dateRange.start, end: dateRange.end });

            // Load coverage in batches to avoid overwhelming the API
            const batchSize = 7;
            for (let i = 0; i < days.length; i += batchSize) {
                const batch = days.slice(i, i + batchSize);
                setTimeout(() => {
                    batch.forEach(day => {
                        // Debounced function returns void, fire and forget
                        debouncedLoadCoverage(day);
                    });
                }, i * 100); // Stagger requests
            }
        }
    }, [dateRange, debouncedLoadCoverage]);

    const handleDragStart = (event: any) => {
        setActiveShift(event.active.data.current);
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        const shift = active.data.current as Shift | null;
        setActiveShift(null);

        if (!over || !shift) return;
        if (displayedVersion?.status !== 'DRAFT') {
            setError(displayedVersion?.status === 'PUBLISHED'
                ? 'Roster is published and locked. Move it back to draft to make changes.'
                : 'Create or select a draft roster for this calendar month before making changes.');
            return;
        }

        try {
            const newDate = new Date(over.id as string);

            // Calculate new start/end times preserving duration
            const oldStart = new Date(shift.startTime);
            const oldEnd = new Date(shift.endTime);
            const duration = oldEnd.getTime() - oldStart.getTime();

            const newStartTime = new Date(newDate);
            newStartTime.setHours(oldStart.getHours(), oldStart.getMinutes());

            const newEndTime = new Date(newStartTime.getTime() + duration);

            // Optimistic update
            const updatedShifts = shifts.map(s =>
                s.id === shift.id
                    ? { ...s, startTime: newStartTime.toISOString(), endTime: newEndTime.toISOString() }
                    : s
            );
            setShifts(updatedShifts);

            // Call API to update shift
            await updateShift(shift.id, {
                startTime: newStartTime.toISOString(),
                endTime: newEndTime.toISOString(),
            });

            // Reload coverage for the affected day (debounced)
            debouncedLoadCoverage(newDate);
        } catch (error: any) {
            console.error("Failed to update shift", error);
            setError(error.message || 'Failed to update shift');
            // Revert optimistic update
            loadShifts(displayedVersion?.id);
        }
    };

    const handlePublishRoster = async () => {
        if (versionActionLoading) return;
        try {
            setVersionActionLoading(true);
            if (!displayedVersion) {
                setError('No version selected');
                return;
            }
            await publishRosterVersion(displayedVersion.id);
            await loadVersions();
            await loadShifts(displayedVersion.id);
        } catch (error: any) {
            setError(error.message || 'Failed to publish roster');
        } finally {
            setVersionActionLoading(false);
        }
    };

    const handleMoveToDraft = async () => {
        if (versionActionLoading) return;
        try {
            setVersionActionLoading(true);
            if (!displayedVersion) {
                setError('No version selected');
                return;
            }
            await unpublishRosterVersion(displayedVersion.id);
            await loadVersions();
            await loadShifts(displayedVersion.id);
            setRefreshCounter(c => c + 1);
        } catch (error: any) {
            setError(error.message || 'Failed to move roster to draft');
        } finally {
            setVersionActionLoading(false);
        }
    };

    // Removed unused variables - MonthlyRosterView handles month view rendering

    const handleMonthChange = (offset: number) => {
        const newDate = new Date(currentDate);
        if (view === 'month') {
            newDate.setMonth(newDate.getMonth() + offset);
        } else {
            newDate.setDate(newDate.getDate() + (offset * 7));
        }
        setCurrentDate(newDate);
    };

    return (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="flex flex-col 2xl:flex-row gap-4 min-h-[calc(100vh-6rem)]">
                {/* Main Calendar Area */}
                <div className="min-w-0 flex-1 overflow-visible">
                    <div className="sticky top-16 z-40 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3 bg-white rounded-lg border border-slate-200 px-4 py-3 shadow-sm">
                        <div className="flex flex-wrap items-center gap-3">
                            <h2 className="text-xl font-bold text-gray-800">
                                {format(currentDate, 'MMMM yyyy')}
                            </h2>
                            {displayedVersion && (
                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${displayedVersion.status === 'PUBLISHED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : displayedVersion.status === 'DRAFT' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                    {displayedVersionLabel} · {displayedVersion.status === 'PUBLISHED' ? 'Published' : displayedVersion.status === 'DRAFT' ? 'Draft' : 'Archived'}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {view === 'month' && (
                                <button
                                    onClick={() => setShowFullscreenRoster(true)}
                                    className="px-3 py-1 border border-blue-200 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium"
                                >
                                    Fullscreen
                                </button>
                            )}
                            <div className="flex bg-gray-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setView('month')}
                                    className={`px-3 py-1 text-sm rounded-md transition-colors ${view === 'month' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Month
                                </button>
                                <button
                                    onClick={() => setView('week')}
                                    className={`px-3 py-1 text-sm rounded-md transition-colors ${view === 'week' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    Week
                                </button>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleMonthChange(-1)}
                                    className="px-3 py-1 border rounded hover:bg-gray-50"
                                >
                                    Prev
                                </button>
                                <button
                                    onClick={() => handleMonthChange(1)}
                                    className="px-3 py-1 border rounded hover:bg-gray-50"
                                >
                                    Next
                                </button>
                            </div>
                            {canCreateRoster && displayedVersion && (
                                displayedVersion.status === 'PUBLISHED' ? (
                                    <button
                                        onClick={handleMoveToDraft}
                                        disabled={versionActionLoading}
                                        className="px-3 py-1 border border-amber-200 rounded bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 font-medium"
                                    >
                                        {versionActionLoading ? 'Moving...' : 'Move to Draft'}
                                    </button>
                                ) : displayedVersion.status === 'DRAFT' ? (
                                    <button
                                        onClick={handlePublishRoster}
                                        disabled={versionActionLoading}
                                        className="px-3 py-1 border border-emerald-200 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 font-medium"
                                    >
                                        {versionActionLoading ? 'Publishing...' : 'Publish'}
                                    </button>
                                ) : null
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}
                    {view === 'month' ? (
                        <MonthlyRosterView
                            currentDate={currentDate}
                            onDateChange={setCurrentDate}
                            versionId={displayedVersion?.id}
                            refreshKey={refreshCounter}
                            onRosterStatusChange={async () => {
                                await loadVersions();
                                setRefreshCounter(c => c + 1);
                            }}
                        />
                    ) : loading ? (
                        <div className="text-center py-10">Loading roster...</div>
                    ) : (
                        <WeeklyRoster shifts={shifts} currentDate={currentDate} onNavigate={handleMonthChange} />
                    )}
                </div>

                {showFullscreenRoster && (
                    <div className="fixed inset-0 z-[70] bg-slate-50 flex flex-col">
                        <div className="h-14 shrink-0 bg-white border-b border-slate-200 px-3 sm:px-5 flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-sm font-bold text-slate-900 truncate">Fullscreen roster</p>
                                <p className="text-xs text-slate-500 truncate">{format(currentDate, 'MMMM yyyy')} · read-only quick view</p>
                            </div>
                            <button
                                onClick={() => setShowFullscreenRoster(false)}
                                className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-100"
                            >
                                Close
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-hidden p-2">
                            <MonthlyRosterView
                                currentDate={currentDate}
                                onDateChange={setCurrentDate}
                                versionId={displayedVersion?.id}
                                refreshKey={refreshCounter}
                                fullscreenReadOnly
                            />
                        </div>
                    </div>
                )}

                {/* Sidebar Widgets - Stacked on mobile, side on desktop */}
                <div className="w-full 2xl:w-72 2xl:shrink-0 flex flex-col md:grid md:grid-cols-2 2xl:flex 2xl:flex-col gap-4">
                    <VersionControlPanel
                        versions={versionsForDisplayedMonth}
                        currentVersion={displayedVersion}
                        onVersionChange={setCurrentVersion}
                        onPublish={handlePublishRoster}
                        onUnpublish={handleMoveToDraft}
                        onRefresh={() => {
                            loadVersions();
                            setRefreshCounter(c => c + 1);
                        }}
                    />

                    <CoverageWidget date={currentDate} />

                    {/* Actions Panel */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <h3 className="font-bold text-gray-800 mb-2">Actions</h3>
                        {canCreateRoster && versionsForDisplayedMonth.length === 0 && (
                            <button
                                onClick={() => setShowWizard(true)}
                                className="w-full mb-2 bg-green-50 text-green-600 px-4 py-2 rounded text-sm font-medium hover:bg-green-100 transition-colors"
                            >
                                Create New Roster
                            </button>
                        )}
                        {canCreateRoster && displayedVersion?.status === 'DRAFT' && (
                            <button
                                onClick={() => setShowPatternModal(true)}
                                className="w-full mb-2 bg-blue-50 text-blue-600 px-4 py-2 rounded text-sm font-medium hover:bg-blue-100 transition-colors"
                            >
                                Generate Pattern
                            </button>
                        )}
                        {canCreateRoster && displayedVersion?.status === 'DRAFT' && (
                            <button
                                onClick={() => setShowBulkModal(true)}
                                className="w-full mb-2 bg-purple-50 text-purple-600 px-4 py-2 rounded text-sm font-medium hover:bg-purple-100 transition-colors"
                            >
                                Bulk Assign
                            </button>
                        )}
                        {canCreateRoster && displayedVersion && (
                            displayedVersion.status === 'PUBLISHED' ? (
                                <button
                                    onClick={handleMoveToDraft}
                                    disabled={versionActionLoading}
                                    className="w-full mb-2 bg-amber-50 text-amber-700 px-4 py-2 rounded text-sm font-medium hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Move to Draft
                                </button>
                            ) : displayedVersion.status === 'DRAFT' ? (
                                <button
                                    onClick={handlePublishRoster}
                                    disabled={versionActionLoading}
                                    className="w-full mb-2 bg-green-50 text-green-600 px-4 py-2 rounded text-sm font-medium hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Publish Roster
                                </button>
                            ) : null
                        )}
                        {canCreateRoster && displayedVersion?.status === 'DRAFT' && (
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="w-full bg-gray-50 text-gray-600 px-4 py-2 rounded text-sm font-medium hover:bg-gray-100 transition-colors border border-gray-200"
                            >
                                + Add Single Shift
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <DragOverlay>
                {activeShift ? (
                    <div className="opacity-80 rotate-2 scale-105 pointer-events-none">
                        <ShiftItem shift={activeShift} />
                    </div>
                ) : null}
            </DragOverlay>

            {showCreateModal && (
                <CreateShiftModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        loadShifts(displayedVersion?.id);
                        setRefreshCounter(c => c + 1);
                        setShowCreateModal(false);
                    }}
                    initialDate={currentDate}
                    versionId={displayedVersion?.id}
                />
            )}

            {showPatternModal && (
                <PatternGenerationModal
                    onClose={() => setShowPatternModal(false)}
                    onSuccess={() => {
                        loadShifts(displayedVersion?.id);
                        setRefreshCounter(c => c + 1);
                        setShowPatternModal(false);
                    }}
                    startDate={currentDate}
                    versionId={displayedVersion?.id}
                />
            )}

            {showBulkModal && (
                <BulkAssignmentModal
                    onClose={() => setShowBulkModal(false)}
                    onSuccess={() => {
                        loadShifts(displayedVersion?.id);
                        setRefreshCounter(c => c + 1);
                        setShowBulkModal(false);
                    }}
                    startDate={currentDate}
                    versionId={displayedVersion?.id}
                />
            )}

            {showWizard && (
                <RosterWizard
                    onClose={() => setShowWizard(false)}
                    onSuccess={() => {
                        loadVersions();
                        loadShifts();
                        setRefreshCounter(c => c + 1);
                        setShowWizard(false);
                    }}
                />
            )}
        </DndContext>
    );
};

export default RosterDashboard;
